import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ArrowLeft, Bus, CreditCard, Store, BarChart3, Wallet, Users, DollarSign } from "lucide-react";
import { Navbar, Footer } from "@/components/site-chrome";
import { LiveBusMap } from "@/components/live-bus-map";
import { PricingEngineDemo } from "@/components/pricing-engine-demo";
import { RevenueFlowDemo } from "@/components/revenue-flow-demo";
import { RevenueModelShowcase } from "@/components/revenue-model-showcase";
import { PlatformArchitecture } from "@/components/platform-architecture";
import { CardSwipe } from "@/components/card-swipe";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const EASE = [0.4, 0, 0.2, 1] as const;

export function DemoPage() {
  const demos = [
    { id: "fleet", label: "Fleet Tracking", icon: Bus },
    { id: "pricing", label: "Pricing Engine", icon: CreditCard },
    { id: "revenue", label: "Revenue Flow", icon: BarChart3 },
    { id: "revenue-model", label: "Revenue Model", icon: DollarSign },
    { id: "architecture", label: "Platform Architecture", icon: Wallet },
    { id: "card-swipe", label: "Card Swipe", icon: Users },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>
        <div className="mt-6">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Interactive Demos</h1>
          <p className="mt-2 text-lg text-muted-foreground">Explore live, interactive demonstrations of BRECCS platform capabilities.</p>
        </div>

        {/* Quick nav */}
        <div className="mt-8 flex flex-wrap gap-2">
          {demos.map(d => (
            <a key={d.id} href={`#${d.id}`} className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold shadow-sm transition hover:shadow-premium">
              <d.icon className="h-4 w-4 text-primary" />
              {d.label}
            </a>
          ))}
        </div>

        {/* Fleet Tracking Demo */}
        <DemoSection id="fleet" title="Real-Time Fleet Tracking" subtitle="Wialon-grade GPS tracking with road-following navigation.">
          <LiveBusMap wialon autoTick height={560} title="Fleet Command Center" canViewTrails canViewHistory />
        </DemoSection>

        {/* Pricing Engine Demo */}
        <DemoSection id="pricing" title="Pricing Engine" subtitle="Dynamic pricing for school services and marketplace items.">
          <PricingEngineDemo />
        </DemoSection>

        {/* Revenue Flow Demo */}
        <DemoSection id="revenue" title="Revenue Flow" subtitle="Visualize how money flows through the education ecosystem.">
          <RevenueFlowDemo />
        </DemoSection>

        {/* Revenue Model Showcase */}
        <DemoSection id="revenue-model" title="Revenue Model Showcase" subtitle="Six revenue streams across the procurement control plane — interactive simulator and scale projection.">
          <RevenueModelShowcase />
        </DemoSection>

        {/* Platform Architecture */}
        <DemoSection id="architecture" title="Platform Architecture" subtitle="The technical foundation powering the BRECCS ecosystem.">
          <PlatformArchitecture />
        </DemoSection>

        {/* Card Swipe */}
        <DemoSection id="card-swipe" title="Smart Card Swipe" subtitle="Tap-to-pay with NFC student cards.">
          <CardSwipe onResolve={(w) => toast.success("Card resolved: " + (w.full_name ?? w.student_id ?? "student") + " · USD " + w.balance_ugx.toLocaleString())} />
        </DemoSection>
      </div>
      <Footer />
    </div>
  );
}

function DemoSection({ id, title, subtitle, children }: { id: string; title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <motion.section
      id={id}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, ease: EASE }}
      className="mt-16 scroll-mt-20"
    >
      <div className="mb-6">
        <h2 className="text-2xl font-bold tracking-tight">{title}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
      </div>
      <div className="rounded-3xl border border-border bg-card p-4 shadow-premium sm:p-6">
        {children}
      </div>
    </motion.section>
  );
}

export default DemoPage;
